import { ReactNode } from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

export const pageShellSx: SxProps<Theme> = {
  width: '100%',
  minWidth: 0,
  maxWidth: 1680,
  mx: 'auto',
  overflowX: 'clip',
};

export const dataTableScrollSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  overflowX: 'auto',
  overflowY: 'auto',
  maxHeight: 'min(60dvh, 640px)',
  overscrollBehaviorX: 'contain',
  WebkitOverflowScrolling: 'touch',
  scrollbarGutter: 'stable',
  contain: 'inline-size',
  '& .MuiTableCell-head, & .RaDatagrid-headerCell': {
    position: 'sticky',
    top: 0,
    zIndex: 3,
    bgcolor: '#F7F9FA',
  },
  '&:focus-visible': {
    outline: '3px solid rgba(14,116,144,.22)',
    outlineOffset: -3,
  },
};

export const resourceListSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  '& .RaList-root, & .RaList-main': { width: '100%', maxWidth: '100%', minWidth: 0 },
  '& .RaList-main': { display: 'flex', flexDirection: 'column', gap: 1.5 },
  '& .RaList-actions': { minHeight: 0, p: 0 },
  '& .RaFilterForm-form': {
    display: 'flex',
    gap: 1.25,
    alignItems: 'center',
    flexWrap: 'wrap',
    p: { xs: 1.25, sm: 1.5 },
    mb: 0,
    borderRadius: '12px',
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    boxShadow: '0 14px 38px -30px rgba(15,42,61,.38)',
    '& .MuiFormControl-root': { minWidth: { xs: '100%', sm: 220 }, m: '0 !important' },
    '& .MuiInputBase-root': { minHeight: 44 },
  },
  '& .RaList-content': {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
    borderRadius: '12px',
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    boxShadow: '0 22px 60px -46px rgba(15,42,61,.48)',
  },
  '& .MuiTableContainer-root': {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    overflowX: 'auto',
    overscrollBehaviorX: 'contain',
    WebkitOverflowScrolling: 'touch',
    scrollbarGutter: 'stable',
    contain: 'inline-size',
  },
  '& .RaDatagrid-root, & .RaDatagrid-tableWrapper': { width: '100%', maxWidth: '100%', minWidth: 0 },
  '& .RaDatagrid-table': { width: 'max-content', minWidth: { xs: '100%', md: 820 }, tableLayout: 'auto' },
  '& .RaDatagrid-headerCell': {
    bgcolor: '#F7F9FA',
    color: '#405466',
    fontWeight: 800,
    fontSize: 12,
    letterSpacing: '.015em',
    py: 1.8,
    borderBottom: '1px solid rgba(14,116,144,.10)',
  },
  '& .RaDatagrid-rowCell': {
    py: 1.7,
    borderBottom: '1px solid rgba(15,23,42,.055)',
    maxWidth: 360,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    verticalAlign: 'middle',
  },
  '& .RaDatagrid-row': {
    transition: 'background-color .18s ease, transform .18s ease',
    '&:hover': { bgcolor: 'rgba(14,116,144,.04)' },
  },
  '& .RaList-pagination': {
    maxWidth: '100%',
    overflow: 'hidden',
    p: { xs: 1, sm: 1.5 },
    borderTop: '1px solid',
    borderColor: 'divider',
    bgcolor: '#FFFFFF',
  },
  '& .MuiTablePagination-toolbar': {
    minHeight: 52,
    px: { xs: 0, sm: 1 },
    flexWrap: { xs: 'wrap', sm: 'nowrap' },
    justifyContent: { xs: 'center', sm: 'flex-end' },
    rowGap: 0.5,
  },
  '& .MuiTablePagination-spacer': { display: { xs: 'none', sm: 'block' } },
  '& .MuiTablePagination-selectLabel': { display: { xs: 'none', sm: 'block' } },
  '& .MuiTablePagination-displayedRows': { fontSize: 12 },
  '& .MuiTablePagination-actions': { ml: { xs: 0, sm: 2 } },
  '& .MuiButton-root': { whiteSpace: 'nowrap' },
};

export const formPageSx: SxProps<Theme> = {
  '& .RaEdit-main, & .RaCreate-main': { mt: 0 },
  '& .RaEdit-card, & .RaCreate-card': {
    overflow: 'visible',
    boxShadow: 'none',
    border: 0,
    bgcolor: 'transparent',
  },
  '& .RaSimpleForm-root': {
    borderRadius: '12px',
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    boxShadow: '0 22px 60px -46px rgba(15,42,61,.48)',
    overflow: 'hidden',
  },
  '& .RaSimpleForm-form': {
    p: { xs: 1.5, sm: 2.25, md: 3 },
  },
  '& .RaToolbar-root': {
    px: { xs: 1.5, sm: 2.25, md: 3 },
    py: 1.5,
    borderTop: '1px solid',
    borderColor: 'divider',
    bgcolor: '#F7F9FA',
  },
};

export const ContentCard = ({
  children,
  sx,
}: {
  children: ReactNode;
  sx?: SxProps<Theme>;
}) => (
  <Card sx={sx ? ([{ overflow: 'hidden' }, sx] as SxProps<Theme>) : { overflow: 'hidden' }}>
    {children}
  </Card>
);

export const SectionHeader = ({
  icon,
  title,
  subtitle,
  action,
}: {
  icon?: ReactNode;
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
      px: { xs: 2, md: 2.5 },
      py: 2,
      borderBottom: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
      {icon && (
        <Box
          sx={{
            width: 38,
            height: 38,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            borderRadius: '12px',
            color: 'primary.main',
            bgcolor: 'rgba(14,116,144,.09)',
            '& svg': { fontSize: 21 },
          }}
        >
          {icon}
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.25 }}>{title}</Typography>
        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
      </Box>
    </Box>
    {action}
  </Box>
);

export const CardBody = ({ children, sx }: { children: ReactNode; sx?: SxProps<Theme> }) => (
  <CardContent sx={sx ? ([{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }, sx] as SxProps<Theme>) : { p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
    {children}
  </CardContent>
);
